import { apiClient } from './client'

export interface User {
  _id: string
  phone: any
  countryCode?: string
  emailVerified: any
  profileImage: any
  id: string
  firstName: string
  lastName: string
  fullName: string
  email: string
  role: string // Role name (e.g., "School Admin")
  institution: string // Institution name (e.g., "Debug Test School") 
  status: string // "Active" or "Inactive"
  lastLogin?: string | null
  createdBy: string // Creator name (e.g., "Test Admin")
  createdAt: string
  // Academic fields for students
  studentId?: string
  gradeId?: string
  streamId?: string
  grade?: {
    name: string
    description?: string
  }
  stream?: {
    name: string
    classTeacher?: string
    gradeId?: string
  }
  // Parent-Student relationship fields
  parentIds?: string[]
  childrenIds?: string[]
  hasParents?: boolean
  hasChildren?: boolean
  // Class teacher assignment fields
  classTeacherAssignments?: Array<{
    streamId: string
    streamName: string
    gradeId: string
    gradeName: string
  }>
  isClassTeacher?: boolean
  // Detailed family information
  parents?: Array<{
    id: string
    fullName: string
    email: string
    phone?: string
    role: string
  }>
  children?: Array<{
    id: string
    fullName: string
    email: string
    phone?: string
    role: string
    studentId?: string
  }>
  familySummary?: {
    parentCount: number
    childrenCount: number
    parentNames?: string
    childrenNames?: string
  }
  // Enhanced academic info for students
  academicSummary?: {
    gradeName?: string
    streamName?: string
    classTeacher?: string
    fullClassInfo?: string
    academicStatus: string
  }
  parentNames?: string[]
  primaryParent?: {
    id: string
    fullName: string
    email: string
    phone?: string
  }
  // Enhanced parent info for parents
  childrenAcademicInfo?: Array<{
    name: string
    studentId?: string
    grade?: string
    stream?: string
    classTeacher?: string
  }>
  parentSummary?: {
    totalChildren: number
    childrenInGrades: string[]
    childrenInStreams: string[]
    allChildrenEnrolled: boolean
  }
  // Onboarding status for check-in types
  onboardingStatus?: {
    biometric: {
      isEnrolled: boolean
      enrolledAt?: string
      fingerPrintCount: number
    }
    rfid: {
      hasCard: boolean
      cardNumber?: string
      cardType?: 'wristband' | 'card' | 'keychain' | 'sticker'
      cardStatus?: 'active' | 'inactive' | 'lost' | 'damaged' | 'suspended'
      hasPinSet: boolean
      batteryLevel?: number
      expiryDate?: string
    }
    smartwatch: {
      hasDevice: boolean
      batteryLevel?: number
    }
    hasBiometric: boolean
    hasRfid: boolean
    hasSmartwatch: boolean
    availableCheckInMethods: string[]
  }
  parentContacts?: Array<{
    name: string
    phone: string
  }>
}

export interface UserStats {
  totalUsers: number
  activeUsers: number
  inactiveUsers: number
  // Dynamic group-based counts (group names as keys)
  [groupName: string]: number
}

export interface CreateUserRequest {
  firstName: string
  lastName: string
  email: string
  roleId: string // Reference to Group (role) ID
  phone?: string
  countryCode?: string
  institutionId?: string // For super admins to specify which institution
  // Academic fields for students
  studentId?: string // Optional - auto-generated by backend if not provided
  gradeId?: string // Grade assignment for students
  streamId?: string // Stream assignment for students
  // Parent-Student relationship fields
  parentIds?: string[] // Parent IDs to link to student
  childrenIds?: string[] // Student IDs to link to parent
  // Driver-specific fields
  licenseNumber?: string
  licenseExpiryDate?: string
  emergencyContact?: string
  emergencyPhone?: string
  vehicleId?: string // Vehicle assignment for drivers
  // password is auto-generated by backend
}

export interface UpdateUserRequest {
  firstName?: string
  lastName?: string
  email?: string
  roleId?: string // Reference to Group (role) ID
  phone?: string
  countryCode?: string
  isActive?: boolean
  // Student-specific fields
  studentId?: string
  gradeId?: string
  streamId?: string
  dateOfBirth?: string
  // Parent/Guardian info
  parentEmail?: string
  parentPhone?: string
}

export interface GetUsersParams {
  page?: number
  limit?: number
  search?: string
  roleId?: string // Filter by role ID instead of role name
  status?: 'active' | 'inactive' // Filter by user status
  sortBy?: 'createdAt' | 'firstName' | 'lastName' | 'email' | 'lastLogin'
  sortOrder?: 'asc' | 'desc'
  institutionId?: string // For super admins to specify which institution
  streamId?: string // Filter by stream ID
}

export interface UsersResponse {
  users: User[]
  pagination: {
    currentPage: number
    totalPages: number
    totalUsers: number
    limit: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}

// Get all users with filtering and pagination
export const getUsers = async (params: GetUsersParams = {}): Promise<UsersResponse> => {
  const searchParams = new URLSearchParams()
  
  if (params.page) searchParams.append('page', params.page.toString())
  if (params.limit) searchParams.append('limit', params.limit.toString())
  if (params.search) searchParams.append('search', params.search)
  if (params.roleId) searchParams.append('roleId', params.roleId)
  if (params.status) searchParams.append('status', params.status)
  if (params.sortBy) searchParams.append('sortBy', params.sortBy)
  if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder)
  if (params.institutionId) searchParams.append('institutionId', params.institutionId)
  if (params.streamId) searchParams.append('streamId', params.streamId)
  
  const response = await apiClient.get(`/users?${searchParams}`)
  return response.data.data
}

// Get single user by ID
export const getUserById = async (userId: string): Promise<{ user: User }> => {
  const response = await apiClient.get(`/users/${userId}`)
  return response.data
}

// Create new user
export const createUser = async (userData: CreateUserRequest): Promise<{ 
  data: {
    user: User; 
    temporaryPassword?: string;
    temporaryPIN?: string;
  };
  message: string;
  success: boolean;
}> => {
  const response = await apiClient.post('/users', userData)
  return response.data
}

// Create family (student + parent) - single atomic operation
export const createFamily = async (familyData: {
  student: {
    firstName: string
    lastName: string
    email: string
    phone?: string
    gradeId: string
    streamId: string
    studentId?: string
  }
  parent: {
    firstName: string
    lastName: string
    email: string
    phone: string
    occupation?: string
    relationship?: string
    address?: string
    emergencyContact?: string
    emergencyPhone?: string
  }
  institutionId?: string // For super admins
}) => {
  const response = await apiClient.post('/users/family', familyData)
  return response.data
}

// Enroll student (with parent) - handles duplicates gracefully 
export const enrolStudent = async (enrollmentData: {
  student: {
    firstName: string
    lastName: string
    email: string
    phone?: string
    gradeId: string
    streamId: string
    studentId?: string
  }
  parent: {
    firstName: string
    lastName: string
    email: string
    phone: string
    occupation?: string
    relationship?: string
    address?: string
    emergencyContact?: string
    emergencyPhone?: string
  }
  institutionId?: string // For super admins
  skipDuplicateEmails?: boolean // Allow updating existing users
}) => {
  const response = await apiClient.post('/users/enrol-student', enrollmentData)
  return response.data
}

// Update user
export const updateUser = async (userId: string, userData: UpdateUserRequest): Promise<{ user: User }> => {
  const response = await apiClient.put(`/users/${userId}`, userData)
  return response.data
}

// Delete user (soft delete)
export const deleteUser = async (userId: string): Promise<{ user: User }> => {
  const response = await apiClient.delete(`/users/${userId}`)
  return response.data
}

// Get user statistics
export const getUserStats = async (institutionId?: string): Promise<UserStats> => {
  const searchParams = new URLSearchParams()
  if (institutionId) searchParams.append('institutionId', institutionId)
  
  const response = await apiClient.get(`/users/stats?${searchParams}`)
  return response.data.data
}

// Reset user password
export const resetUserPassword = async (userId: string): Promise<{ message: string }> => {
  const response = await apiClient.post(`/users/${userId}/reset-password`)
  return response.data
}

// Toggle user activation status
export const toggleUserStatus = async (userId: string): Promise<{ user: User }> => {
  const response = await apiClient.patch(`/users/${userId}/toggle-status`)
  return response.data
}

// Get basic user information by ID (for displaying creator names, etc.)
export const getUserBasicInfo = async (userId: string): Promise<{
  id: string
  firstName: string
  lastName: string
  fullName: string
  email: string
} | null> => {
  try {
    if (!userId) return null
    
    const response = await apiClient.get(`/users/${userId}`)
    const user = response.data.user
    
    return {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      fullName: user.fullName || `${user.firstName} ${user.lastName}`,
      email: user.email
    }
  } catch (error) {
    console.error('Failed to fetch user basic info:', error)
    return null
  }
}

// Get multiple users' basic information by IDs
export const getUsersBasicInfo = async (userIds: string[]): Promise<{
  id: string
  firstName: string
  lastName: string
  fullName: string
  email: string
}[]> => {
  try {
    if (!userIds || userIds.length === 0) return []

    // For now, we'll fetch them individually. In a real app, you might want a batch endpoint
    const users = await Promise.all(
      userIds.map(async (id) => {
        try {
          return await getUserBasicInfo(id)
        } catch {
          return null
        }
      })
    )

    return users.filter((user): user is NonNullable<typeof user> => user !== null)
  } catch (error) {
    console.error('Failed to fetch users basic info:', error)
    return []
  }
}

// =================== BIOMETRIC OPERATIONS ===================

// Get biometric status for a user
export const getBiometricStatus = async (userId: string): Promise<{
  success: boolean
  data: {
    user: {
      id: string
      firstName: string
      lastName: string
      fullName: string
      email: string
      studentId?: string
    }
    biometric: {
      isEnrolled: boolean
      enrolledAt?: string
      lastVerified?: string
      fingerPrintCount: number
      externalId?: string
    } | null
  }
}> => {
  const response = await apiClient.get(`/users/biometric/${userId}`)
  return response.data
}

// Register biometric (fingerprints) for a user
export const registerBiometric = async (data: {
  userId: string
  fingerPrints: string[] // Base64 encoded fingerprint templates
}): Promise<{
  success: boolean
  message: string
  data?: {
    userId: string
    isEnrolled: boolean
    enrolledAt: string
    fingerPrintCount: number
  }
}> => {
  const response = await apiClient.post('/users/register-bio', data)
  return response.data
}

// Delete biometric for a user
export const deleteBiometric = async (userId: string): Promise<{
  success: boolean
  message: string
  data?: {
    userId: string
    userName: string
    deletedAt: string
  }
}> => {
  const response = await apiClient.delete(`/users/biometric/${userId}`)
  return response.data
}

// =================== RFID CARD OPERATIONS ===================

// Get RFID cards (with optional filters)
export const getRFIDCards = async (params?: {
  page?: number
  limit?: number
  status?: string
  cardType?: string
  assignedToId?: string
  assigned?: boolean // true = only assigned, false = only unassigned, undefined = all
}): Promise<{
  success: boolean
  data: Array<{
    id: string
    cardNumber: string
    rfidTag: string
    cardType: 'wristband' | 'card' | 'keychain' | 'sticker'
    status: 'active' | 'inactive' | 'lost' | 'damaged' | 'suspended'
    assignedToId?: string
    assignedToName?: string
    assignedToType?: string
    institutionId: string
    institutionName: string
    issueDate: string
    expiryDate?: string
    lastUsed?: string
    totalScans: number
    dailyScans: number
    batteryLevel?: number
    notes?: string
    createdBy: string
    createdAt: string
    updatedAt: string
  }>
  message: string
}> => {
  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.append('page', params.page.toString())
  if (params?.limit) searchParams.append('limit', params.limit.toString())
  if (params?.status) searchParams.append('status', params.status)
  if (params?.cardType) searchParams.append('cardType', params.cardType)
  if (params?.assignedToId) searchParams.append('assignedToId', params.assignedToId)
  if (params?.assigned !== undefined) searchParams.append('assigned', params.assigned.toString())

  const response = await apiClient.get(`/rfid/cards?${searchParams}`)
  return response.data
}

// Assign RFID card to user
export const assignRFIDCard = async (cardId: string, data: {
  assignedToId: string
  assignedToType: 'student' | 'teacher' | 'parent' | 'driver' | 'admin'
}): Promise<{
  success: boolean
  message: string
  data?: {
    card: any
    pinGenerated?: boolean
  }
}> => {
  const response = await apiClient.post(`/rfid/cards/${cardId}/assign`, data)
  return response.data
}

// Unassign RFID card from user
export const unassignRFIDCard = async (cardId: string): Promise<{
  success: boolean
  message: string
}> => {
  const response = await apiClient.post(`/rfid/cards/${cardId}/unassign`)
  return response.data
}

// Reset card PIN (admin action)
export const resetCardPIN = async (cardId: string): Promise<{
  success: boolean
  message: string
  data?: {
    newPin: string
  }
}> => {
  const response = await apiClient.post(`/rfid/cards/${cardId}/pin/reset`)
  return response.data
}

// Get card by card number (for PIN reset and other operations)
export const getCardByNumber = async (cardNumber: string): Promise<{
  success: boolean
  data: {
    id: string
    cardNumber: string
    rfidTag: string
    cardType: string
    status: string
    assignedToId?: string
    assignedToName?: string
  } | null
  message: string
}> => {
  // Use the search parameter to find the card
  const response = await apiClient.get(`/rfid/cards?search=${encodeURIComponent(cardNumber)}`)
  // Find exact match from results
  const cards = response.data.data || []
  const exactMatch = cards.find((card: { cardNumber: string }) => card.cardNumber === cardNumber)
  return {
    success: response.data.success,
    data: exactMatch || null,
    message: exactMatch ? 'Card found' : 'Card not found'
  }
}

// Get unassigned cards for assignment dropdown (automatically filtered by user's institution)
export const getUnassignedCards = async (cardType?: string): Promise<{
  success: boolean
  data: Array<{
    id: string
    cardNumber: string
    rfidTag: string
    cardType: string
    status: string
  }>
  message: string
}> => {
  const params = new URLSearchParams()
  // assigned=false returns cards without an assignedToId
  params.append('assigned', 'false')
  if (cardType) params.append('cardType', cardType)

  const response = await apiClient.get(`/rfid/cards?${params}`)
  return response.data
}

// =================== USER LOOKUP OPERATIONS ===================

export interface UserLookupResult {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string
}

// Lookup user by email or phone - returns user ID (useful for bulk operations)
export const lookupUserByContact = async (params: {
  email?: string
  phone?: string
}): Promise<{
  success: boolean
  data: UserLookupResult | null
  message: string
}> => {
  const searchParams = new URLSearchParams()
  if (params.email) searchParams.append('email', params.email)
  if (params.phone) searchParams.append('phone', params.phone)

  try {
    const response = await apiClient.get(`/users/lookup?${searchParams}`)
    return response.data
  } catch (error: any) {
    // Return null data for 404 (user not found) instead of throwing
    if (error.response?.status === 404) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || 'User not found'
      }
    }
    throw error
  }
}